/**
 * Pure rules behind the location (établissement) dialog. The server enforces
 * the same constraints (LocationResource, LocationProcessor); checking them
 * here gives immediate, field-level feedback.
 */
import type { Location, LocationType } from "@/api/types";

export const LOCATION_CODE_MAX_LENGTH = 50;
const CODE_PATTERN = /^[a-z0-9-]+$/;

export const LOCATION_TYPE_LABELS: Record<LocationType, string> = {
  network: "Réseau",
  library: "Bibliothèque",
  branch: "Antenne",
  section: "Section",
};

export const LOCATION_TYPE_DESCRIPTIONS: Record<LocationType, string> = {
  network: "Regroupe plusieurs bibliothèques d'un territoire.",
  library: "Un équipement ouvert au public.",
  branch: "Un point de prêt rattaché à une bibliothèque.",
  section: "Un espace ou un fonds au sein d'un établissement.",
};

/** Lowercase, accent-free, dash-separated: the format the API accepts. */
export function locationCodeFromName(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, LOCATION_CODE_MAX_LENGTH)
    .replace(/-+$/, "");
}

export interface LocationDraft {
  name: string;
  code: string;
}

export type LocationErrors = Partial<Record<keyof LocationDraft, string | undefined>>;

export function validateLocation(
  draft: LocationDraft,
  locations: Location[],
  editingId: string | null
): LocationErrors {
  const errors: LocationErrors = {};
  const name = draft.name.trim();
  const code = draft.code.trim();

  if (name.length < 2) errors.name = "Le nom doit compter au moins 2 caractères.";

  if (code.length < 2) {
    errors.code = "Le code doit compter au moins 2 caractères.";
  } else if (!CODE_PATTERN.test(code)) {
    errors.code = "Minuscules, chiffres et tirets uniquement.";
  } else if (locations.some((l) => l.code === code && l.id !== editingId)) {
    errors.code = "Ce code est déjà utilisé par un autre établissement.";
  }

  return errors;
}

/**
 * Locations that may become the parent of `editingId`: anything but the
 * location itself and its descendants, which would create a cycle.
 */
export function parentCandidates(locations: Location[], editingId: string | null): Location[] {
  const excluded = new Set<string>();
  if (editingId !== null) {
    excluded.add(editingId);
    let added = true;
    while (added) {
      added = false;
      for (const location of locations) {
        if (location.parentId && excluded.has(location.parentId) && !excluded.has(location.id)) {
          excluded.add(location.id);
          added = true;
        }
      }
    }
  }

  return locations
    .filter((l) => !excluded.has(l.id))
    .sort((a, b) => a.depth - b.depth || a.name.localeCompare(b.name, "fr"));
}
